import { SIM_SELECTION_STATE } from '@apps/redux/features/SimSelectionSlice'
import {
  MAX_INPUT_LENGTH,
  sanitizeTextField,
  isDigitOrEmpty,
  formatSimField
} from '@apps/utils/formHelpers'

export const SIM_SELECTION_EVENTS = {
  SIM: 'event.target.simSelection.sim'
}

// The in-flight validation for the current SIM number, so re-validating an
// unchanged number is free. Five paths run this validation — mount, language change, field blur, the
// Continue click and proceedToNextStep — so tabbing out of the field and then
// clicking Continue used to call the API twice for the same digits.
//
// Only the outcome is cached, never the error copy: the language-change path
// re-runs this to re-localize the message, and must still get the current
// wording without paying for a request.
//
// Only the most recent number is held. Alternating between two SIMs revalidates
// each time, which is correct if slower — the point is to stop one number being
// checked repeatedly, not to remember every number a user has typed.
// The promise is held rather than the resolved outcome: the field's onBlur
// request can still be in flight when Continue triggers another validation, and
// both would otherwise miss a cache that is only written once a response lands.
let validation = { simNumber: null, request: null }

// Not cleared on logout: both session cards navigate with window.location.href,
// a full document load that tears down this module and its cache along with it.
// The export exists for tests, which share one module instance across cases.
export const resetSimValidationCache = () => {
  validation = { simNumber: null, request: null }
}

export default {
  [SIM_SELECTION_EVENTS.SIM]: {
    onChange: (value, updateSimSelectionState) => {
      const sanitizedValue = sanitizeTextField(value)
      if (isDigitOrEmpty(sanitizedValue)) {
        const { formattedSim, simArr } = formatSimField(sanitizedValue)
        if (simArr && simArr.length > 0) {
          updateSimSelectionState({
            [SIM_SELECTION_STATE.SIM]: formattedSim
          })
        } else {
          updateSimSelectionState({
            [SIM_SELECTION_STATE.SIM]: ''
          })
        }
      }
    },
    onBlur: async (state, isSimValid, setLoading, errors, updateSimSelectionState) => {
      let error = null
      const simNumber = state[SIM_SELECTION_STATE.SIM] || ''

      if (simNumber === '') {
        error = errors.simRequiredError
      } else if (simNumber.length !== MAX_INPUT_LENGTH) {
        error = errors.simLengthError
      } else if (simNumber.length === MAX_INPUT_LENGTH) {
        if (validation.simNumber !== simNumber) {
          // perform service call
          validation = {
            simNumber,
            request: Promise.resolve(isSimValid(simNumber))
              .then((result) => Boolean(result))
              .catch(() => false)
          }
          setLoading(true)
          const isValid = await validation.request
          error = isValid ? null : errors.simUnavailableError
          setLoading(false)
        } else {
          const isValid = await validation.request
          error = isValid ? null : errors.simUnavailableError
        }
      } else {
        error = null
      }
      updateSimSelectionState({
        [SIM_SELECTION_STATE.SIM_ERROR]: error
      })
      return error !== null
    }
  }
}
